/**
 * useSavedPrompts — the viewer's prompt library, live and optimistic
 * (2026-09-17). The `useThreadCategories` contract: the local list changes at
 * once, the write settles behind it, and a failed write is undone by the next
 * snapshot rather than by bookkeeping nobody can see.
 *
 * This hook is where `firebase/auth` enters the graph (through the repository's
 * write guard), which is why the picker takes the API it returns as a PROP
 * instead of importing it: a component the chat-isolation suites render must
 * not reach `firebase/auth`, whose import touches global `fetch` and takes the
 * whole suite down before a single test runs (2026-09-11). The
 * `RoutineManagerPanel` / `AccountRoutinesApi` arrangement, for the same reason.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSavedPrompt,
  deleteSavedPrompt,
  markSavedPromptUsed,
  renamePromptCategory,
  subscribeSavedPrompts,
  updateSavedPrompt,
} from '../repositories/savedPromptsRepository';
import {
  MAX_PROMPTS_PER_PERSON,
  orderPrompts,
  promptCategories,
  type PromptProblem,
  type SavedPrompt,
  type SavedPromptDraft,
} from '../types/savedPrompts';

/** What the picker and the editor are handed. Everything they can do is here. */
export interface SavedPromptsApi {
  /** Ordered as the picker shows them: recently used, then recently changed. */
  prompts: SavedPrompt[];
  /** Distinct category names over those prompts, folded and alphabetical. */
  categories: string[];
  /** True until the first snapshot lands. Absence proves nothing before then. */
  loading: boolean;
  /** The last read failed; the list below is the last one we knew. */
  degraded: boolean;
  /** The library can take another prompt. */
  hasRoom: boolean;
  max: number;
  create: (draft: SavedPromptDraft) => Promise<PromptProblem | null>;
  update: (id: string, draft: SavedPromptDraft) => Promise<PromptProblem | null>;
  remove: (id: string) => Promise<boolean>;
  renameCategory: (from: string, to: string) => Promise<boolean>;
  /** Record that a prompt was just put into a draft. Never awaited by a caller. */
  markUsed: (id: string) => void;
}

const EMPTY: SavedPrompt[] = [];

export function useSavedPrompts(uid: string | null | undefined): SavedPromptsApi {
  const [prompts, setPrompts] = useState<SavedPrompt[]>(EMPTY);
  const [loading, setLoading] = useState<boolean>(Boolean(uid));
  const [degraded, setDegraded] = useState(false);
  const promptsRef = useRef<SavedPrompt[]>(EMPTY);
  promptsRef.current = prompts;

  useEffect(() => {
    if (!uid) {
      setPrompts(EMPTY);
      setLoading(false);
      setDegraded(false);
      return undefined;
    }
    setLoading(true);
    let live = true;
    let stop: (() => void) | undefined;
    try {
      stop = subscribeSavedPrompts(
        uid,
        (rows) => {
          if (!live) return;
          setPrompts(rows);
          setLoading(false);
          setDegraded(false);
        },
        () => {
          if (!live) return;
          // Keep whatever we already showed — a picker that blanks mid-compose
          // is worse than one that is briefly stale.
          setLoading(false);
          setDegraded(true);
        },
      );
    } catch {
      setLoading(false);
      setDegraded(true);
    }
    return () => { live = false; stop?.(); };
  }, [uid]);

  /** Apply now, let the snapshot settle it, and let a failure undo it. */
  const optimistic = useCallback((next: SavedPrompt[]) => {
    setPrompts(orderPrompts(next));
  }, []);

  const create = useCallback(async (draft: SavedPromptDraft): Promise<PromptProblem | null> => {
    if (!uid) return 'empty-body';
    const verdict = await createSavedPrompt(uid, draft, promptsRef.current.length).catch(() => null);
    if (!verdict) return 'library-full';
    if (!verdict.ok) return verdict.problem;
    optimistic([...promptsRef.current, verdict.value]);
    return null;
  }, [uid, optimistic]);

  const update = useCallback(async (id: string, draft: SavedPromptDraft): Promise<PromptProblem | null> => {
    if (!uid) return 'empty-body';
    const verdict = await updateSavedPrompt(uid, id, draft).catch(() => null);
    if (!verdict) return 'empty-body';
    if (!verdict.ok) return verdict.problem;
    optimistic(promptsRef.current.map((p) => (p.id === id ? { ...p, ...verdict.value } : p)));
    return null;
  }, [uid, optimistic]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!uid) return false;
    const before = promptsRef.current;
    optimistic(before.filter((p) => p.id !== id));
    try {
      await deleteSavedPrompt(uid, id);
      return true;
    } catch {
      optimistic(before);
      return false;
    }
  }, [uid, optimistic]);

  const renameCategory = useCallback(async (from: string, to: string): Promise<boolean> => {
    if (!uid) return false;
    try {
      await renamePromptCategory(uid, from, to);
      return true;
    } catch {
      return false;
    }
  }, [uid]);

  const markUsed = useCallback((id: string) => {
    if (!uid) return;
    const row = promptsRef.current.find((p) => p.id === id);
    markSavedPromptUsed(uid, id, row?.useCount ?? 0);
    // Reflected locally too, so the order the person sees matches what they just
    // did without waiting on a round trip.
    optimistic(promptsRef.current.map((p) => (
      p.id === id ? { ...p, lastUsedAt: Date.now(), useCount: (p.useCount ?? 0) + 1 } : p
    )));
  }, [uid, optimistic]);

  const categories = useMemo(() => promptCategories(prompts), [prompts]);

  return useMemo<SavedPromptsApi>(() => ({
    prompts,
    categories,
    loading,
    degraded,
    hasRoom: prompts.length < MAX_PROMPTS_PER_PERSON,
    max: MAX_PROMPTS_PER_PERSON,
    create,
    update,
    remove,
    renameCategory,
    markUsed,
  }), [prompts, categories, loading, degraded, create, update, remove, renameCategory, markUsed]);
}
